// src/contexts/supabaseUser.context.js

import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { supabase, auth } from "../supabase/client";
import { get } from "lodash";
import tokenManager from "../utils/security/tokenManager";
import {
  DEFAULT_INACTIVITY_MS,
  resolveAutoLogoutMs,
} from "../utils/security/sessionPolicy";

// ────────── Create Context ──────────
export const SupabaseUserContext = createContext();

export const SupabaseUserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const logoutTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const timerStartTimeRef = useRef(0);
  const inactivityTimeoutRef = useRef(DEFAULT_INACTIVITY_MS);
  const sessionPolicyRef = useRef({ extended: false, inactivityTimeoutMs: DEFAULT_INACTIVITY_MS });
  const [sessionPolicy, setSessionPolicyState] = useState(sessionPolicyRef.current);
  const [inactivityDialogOpen, setInactivityDialogOpen] = useState(false);
  const [inactivityTimeLeft, setInactivityTimeLeft] = useState(0);

  const clearInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    timerStartTimeRef.current = 0;
  };

  const scheduleInactivityTimer = (ms) => {
    clearInactivityTimer();
    timerStartTimeRef.current = Date.now();
    inactivityTimerRef.current = setTimeout(() => {
      setInactivityDialogOpen(true);
    }, ms);
  };

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    scheduleInactivityTimer(inactivityTimeoutRef.current);
  }, []);

  const setSessionPolicy = useCallback((policy) => {
    const next = policy || { extended: false, inactivityTimeoutMs: DEFAULT_INACTIVITY_MS };
    sessionPolicyRef.current = next;
    inactivityTimeoutRef.current = next.inactivityTimeoutMs || DEFAULT_INACTIVITY_MS;
    setSessionPolicyState(next);
    if (user) {
      resetInactivityTimer();
    }
    auth.getSession().then(({ data: { session } }) => {
      if (session) {
        applyAutoLogoutTimer(session);
      }
    });
  }, [user, resetInactivityTimer]);

  // Update inactivity time left every second - only when dialog is open (avoids re-rendering whole app every second)
  useEffect(() => {
    if (!user || !inactivityDialogOpen) return;

    const interval = setInterval(() => {
      if (timerStartTimeRef.current > 0) {
        const now = Date.now();
        const elapsed = now - timerStartTimeRef.current;
        const timeoutMs = inactivityTimeoutRef.current;
        const timeLeft = Math.max(0, Math.ceil((timeoutMs - elapsed) / 1000));
        setInactivityTimeLeft(timeLeft);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [user, inactivityDialogOpen]);

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    const handleActivity = () => resetInactivityTimer();

    events.forEach(event => document.addEventListener(event, handleActivity));
    
    // Start the initial inactivity timer
    resetInactivityTimer();

    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
      clearInactivityTimer();
    };
    // resetInactivityTimer is stable for this effect's purpose (re-init on user session only)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ────────── SCHEDULE / CLEAR AUTO-LOGOUT ──────────
  function scheduleAutoLogout(ms) {
    clearAutoLogoutTimer();
    if (ms == null || ms <= 0) return;
    logoutTimerRef.current = setTimeout(() => {
      logOutUser().finally(() => {
        window.location.href = "/login";
      });
    }, ms);
  }

  function clearAutoLogoutTimer() {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }

  function applyAutoLogoutTimer(session) {
    const loginTs = parseInt(localStorage.getItem("loginTimestamp") || "", 10);
    const ms = resolveAutoLogoutMs(
      session,
      sessionPolicyRef.current,
      Number.isFinite(loginTs) ? loginTs : null
    );
    if (ms != null && ms > 0) {
      scheduleAutoLogout(ms);
    }
  }

  // ────────── LOGIN ──────────
  const emailPasswordLogin = async (email, password) => {
    try {
      email = email.trim().toLowerCase();
      
      // Sign in to Supabase
      const { data, error } = await auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Enhanced error handling for specific Supabase errors
        if (error.message?.includes('Email not confirmed') || error.message?.includes('email_not_confirmed')) {
          throw new Error('Please check your email and click the confirmation link before logging in.');
        } else if (error.message?.includes('Invalid login credentials') || error.message?.includes('invalid_credentials')) {
          throw new Error('Invalid email or password.');
        } else if (error.message?.includes('Too many requests') || error.message?.includes('rate_limit_exceeded')) {
          throw new Error('Too many login attempts. Please try again later.');
        } else if (error.message?.includes('User not found')) {
          throw new Error('No account found with this email address.');
        } else if (error.message?.includes('Password')) {
          throw new Error('Invalid password.');
        } else {
          // For any other error, show the actual Supabase error message
          throw new Error(error.message || 'Login failed. Please try again.');
        }
      }

      setUser(data.user);

      // Store the Supabase JWT securely using token manager
      const tokenStored = tokenManager.setToken(data.session.access_token);
      if (!tokenStored) {
        throw new Error("Failed to store authentication token securely");
      }

      // Store user info securely
      tokenManager.setCurrentUser({
        id: data.user.id,
        name: get(data.user, "user_metadata.name", ""),
        email: data.user.email,
      });

      applyAutoLogoutTimer(data.session);

      return data.user;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  /** Supabase duplicate signup (anti-enumeration): empty/missing identities, no new session. */
  const isDuplicateSignupResponse = (data) => {
    const user = data?.user;
    if (!user) return false;

    const identities = user.identities;
    if (Array.isArray(identities) && identities.length === 0) {
      return true;
    }
    // Ambiguous response: no session and no linked identity — do not treat as a fresh signup.
    if (!data.session && (!Array.isArray(identities) || identities.length === 0)) {
      return true;
    }
    return false;
  };

  // ────────── SIGNUP ──────────
  const emailPasswordSignup = async (email, password, userData = {}) => {
    try {
      email = email.trim().toLowerCase();

      const { data, error } = await auth.signUp({
        email,
        password,
        options: {
          data: userData, // Additional user metadata
        },
      });

      if (error) {
        const msg = (error.message || "").toLowerCase();
        const code = error.code || "";
        if (
          code === "user_already_exists" ||
          code === "email_exists" ||
          msg.includes("already registered") ||
          msg.includes("already been registered")
        ) {
          const dupErr = new Error(
            "An account with this email already exists. Use Forgot password or sign in — do not register again."
          );
          dupErr.code = "user_already_registered";
          throw dupErr;
        }
        throw error;
      }

      if (isDuplicateSignupResponse(data)) {
        const dupErr = new Error(
          "An account with this email already exists. Use Forgot password or sign in — do not register again."
        );
        dupErr.code = "user_already_registered";
        throw dupErr;
      }

      const authUserId = data?.user?.id || null;

      // Auto-login after signup if email confirmation is not required
      if (data.user && data.session) {
        const loggedInUser = await emailPasswordLogin(email, password);
        return {
          authUserId: loggedInUser?.id || authUserId,
          user: loggedInUser,
          session: data.session,
        };
      }

      return {
        authUserId,
        user: data.user,
        session: data.session ?? null,
      };
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  };

  // ────────── FETCH USER (ON PAGE LOAD) ──────────
  const fetchUser = async () => {
    try {
      // Get current session
      const { data: { session }, error } = await auth.getSession();
      
      if (error) {
        console.error("Error getting session:", error);
        setUser(null);
        return false;
      }

      if (!session || !session.user) {
        setUser(null);
        return false;
      }

      // Copy the live Supabase session into realmJwt before validating.
      // A missing local copy must not sign out a session that can still refresh.
      if (session.access_token) {
        tokenManager.setToken(session.access_token);
      }

      if (!tokenManager.isTokenValid()) {
        await logOutUser();
        return false;
      }

      setUser(session.user);

      let loginTimestampMs = null;
      try {
        loginTimestampMs = parseInt(localStorage.getItem("loginTimestamp") || "", 10) || null;
      } catch {
        loginTimestampMs = null;
      }
      const autoLogoutMs = resolveAutoLogoutMs(
        session,
        sessionPolicyRef.current,
        loginTimestampMs
      );
      if (autoLogoutMs != null) {
        if (autoLogoutMs > 0) {
          scheduleAutoLogout(autoLogoutMs);
        } else {
          await logOutUser();
          return false;
        }
      }

      return session.user;
    } catch (error) {
      console.error("Error fetching user:", error);
      setUser(null);
      return false;
    }
  };

  // ────────── LOGOUT ──────────
  const logOutUser = async () => {
    try {
      // Prefer local-only sign-out so we don't call /logout when session is already missing (avoids 403 / AuthSessionMissingError noise).
      const { error } = await auth.signOut({ scope: "local" });
      if (error) {
        console.warn("Error signing out (local):", error);
      }
    } catch (err) {
      console.warn("signOut failed (continuing cleanup):", err);
    }

    try {
      tokenManager.clearTokens();
      setUser(null);
      clearAutoLogoutTimer();
      clearInactivityTimer();
    } catch (cleanupErr) {
      console.warn("Logout cleanup:", cleanupErr);
    }

    return true;
  };

  // ────────── INACTIVITY DIALOG HANDLERS ──────────
  const handleStayLoggedIn = () => {
    setInactivityDialogOpen(false);
    resetInactivityTimer();
  };

  const handleLogoutNow = async () => {
    setInactivityDialogOpen(false);
    await logOutUser();
    window.location.href = "/login";
  };

  // ────────── LISTEN TO AUTH CHANGES ──────────
  useEffect(() => {
    const { data: { subscription } } = auth.onAuthStateChange(
      async (event, session) => {
        if (event === "INITIAL_SESSION") {
          if (session?.user && tokenManager.setToken(session.access_token)) {
            setUser(session.user);
            applyAutoLogoutTimer(session);
          } else {
            setUser(null);
          }
          setAuthReady(true);
          return;
        }
        if (event === 'SIGNED_IN' && session) {
          if (tokenManager.setToken(session.access_token)) {
            setUser(session.user);
            applyAutoLogoutTimer(session);
          } else {
            setUser(null);
          }
          setAuthReady(true);
        } else if (event === 'TOKEN_REFRESHED' && session) {
          tokenManager.setToken(session.access_token);
          if (!sessionPolicyRef.current.extended) {
            applyAutoLogoutTimer(session);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          tokenManager.clearTokens();
          clearAutoLogoutTimer();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // ────────── CONTEXT VALUE ──────────
  const contextValue = {
    user,
    authReady,
    emailPasswordLogin,
    emailPasswordSignup,
    logOutUser,
    fetchUser,
    sessionPolicy,
    setSessionPolicy,
    inactivityDialogOpen,
    setInactivityDialogOpen,
    inactivityTimeLeft,
    handleStayLoggedIn,
    handleLogoutNow,
    resetInactivityTimer, // For testing purposes
  };

  return (
    <SupabaseUserContext.Provider value={contextValue}>
      {children}
    </SupabaseUserContext.Provider>
  );
};
